import { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '@/context/ThemeContext';
import { Download, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadMagnet() {
  const { theme } = useTheme();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [form, setForm] = useState({ name: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        source: 'lead_magnet',
        metadata: { type: 'checklist' },
      });
      await axios.post(`${API}/email`, {
        email: form.email,
        name: form.name,
        type: 'checklist',
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setLoading(false);
  };

  return (
    <section
      data-testid="lead-magnet-section"
      ref={ref}
      style={{ backgroundColor: theme === 'dark' ? '#0C1120' : '#F0EDE8' }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
          >
            <span className="font-mono text-xs tracking-[0.2em] uppercase block mb-4" style={{ color: 'var(--accent-primary)' }}>
              Free Resource
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-4" style={{ color: 'var(--text-primary)' }}>
              Checklist "Kesiapan Digital Bisnis"
            </h2>
            <p className="text-base leading-relaxed mb-6" style={{ color: 'var(--text-secondary)' }}>
              Panduan praktis untuk mengevaluasi kesiapan digital bisnis Anda. Termasuk framework penilaian, action items prioritas, dan template perencanaan.
            </p>
            <ul className="space-y-3">
              {[
                'Framework penilaian 5 dimensi digital',
                'Checklist teknis website modern',
                'Template narasi brand digital',
                'Panduan pemilihan tech stack',
                'Action plan 30 hari pertama',
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-sm" style={{ color: 'var(--text-primary)' }}>
                  <CheckCircle2 size={16} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            className="glass-card p-8"
            initial={{ opacity: 0, x: 30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            {submitted ? (
              <div className="text-center space-y-4 py-8" data-testid="lead-magnet-success">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto" style={{ background: 'rgba(0,255,133,0.1)' }}>
                  <Mail size={28} style={{ color: 'var(--accent-primary)' }} />
                </div>
                <h3 className="font-heading text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Terima Kasih!
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Checklist akan dikirim ke email Anda. Cek inbox (dan spam folder) dalam beberapa menit.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-magnet-form">
                <div className="flex items-center gap-3 mb-2">
                  <Download size={20} style={{ color: 'var(--accent-primary)' }} />
                  <h3 className="font-heading text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    Download Gratis
                  </h3>
                </div>

                <div>
                  <label className="font-mono text-xs tracking-wider uppercase block mb-2" style={{ color: 'var(--text-secondary)' }}>Nama</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    data-testid="lead-magnet-name"
                    placeholder="Nama Anda"
                    className="w-full px-4 py-3 rounded-xl text-sm"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      border: '1px solid var(--border-card)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>

                <div>
                  <label className="font-mono text-xs tracking-wider uppercase block mb-2" style={{ color: 'var(--text-secondary)' }}>Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    data-testid="lead-magnet-email"
                    placeholder="email@bisnis-anda.com"
                    required
                    className="w-full px-4 py-3 rounded-xl text-sm"
                    style={{
                      backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                      border: '1px solid var(--border-card)',
                      color: 'var(--text-primary)',
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading || !form.email}
                  data-testid="lead-magnet-submit"
                  className="w-full py-4 rounded-xl font-heading font-semibold text-sm tracking-wide uppercase flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 disabled:opacity-40"
                  style={{
                    backgroundColor: 'var(--accent-primary)',
                    color: theme === 'dark' ? '#0D0D0D' : '#FFFFFF',
                  }}
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                  Kirim Checklist ke Email Saya
                </button>

                <p className="text-[10px] text-center" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                  Kami menghormati privasi Anda. Tidak ada spam, selamanya.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
